import React, { useState, useEffect } from 'react';
import { getCompletionById } from '../utils/quizSessionManager';
import { getOriginalQuestionNumber, getWrongAnswersForReport } from '../utils/quizReport';
import './ReportView.css';

const ReportView = () => {
  const [completion, setCompletion] = useState(null);
  const [loading, setLoading] = useState(false);

  const id = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('id')
    : null;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setCompletion(null);
    getCompletionById(id)
      .then((data) => {
        if (!cancelled) setCompletion(data);
      })
      .catch(() => {
        if (!cancelled) setCompletion(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  if (!id) {
    return (
      <div className="report-view-container">
        <div className="report-view-card">
          <p className="report-view-message">No report specified.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="report-view-container">
        <div className="report-view-card">
          <div className="loading-spinner" />
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  if (!completion) {
    return (
      <div className="report-view-container">
        <div className="report-view-card">
          <p className="report-view-message">Report not found.</p>
        </div>
      </div>
    );
  }

  const completedAt = completion.completedAt?.toDate?.();
  const score = completion.score ?? 0;
  const total = completion.totalQuestions ?? 0;
  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
  const allAnswers = completion.answers || [];
  const wrongAnswers = getWrongAnswersForReport(allAnswers);
  const correctCount = allAnswers.length - wrongAnswers.length;

  return (
    <div className="report-view-container">
      <div className="report-view-card">
        <h1 className="report-view-report-title">{completion.sessionTitle || 'Quiz'} — Report</h1>
        <div className="report-view-meta">
          <span><strong>User:</strong> {completion.userEmail || '—'}</span>
          {(completion.playerName || '').trim() && (
            <span><strong>Name:</strong> {completion.playerName.trim()}</span>
          )}
          {completedAt && (
            <span><strong>Completed:</strong> {completedAt.toLocaleString()}</span>
          )}
        </div>

        <div className="report-view-score-block">
          <div className="report-view-score-text">
            Score: {score} / {total} ({percentage}%)
          </div>
          <div className="report-view-progress-bar">
            <div
              className="report-view-progress-fill"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>

        {allAnswers.length === 0 ? (
          <p className="report-view-no-answers">No per-question data for this completion.</p>
        ) : wrongAnswers.length === 0 ? (
          <p className="report-view-no-answers">All {correctCount} answers correct — no wrong answers to review.</p>
        ) : (
          <div className="report-view-table-wrap">
            <h3 className="report-view-section-title">
              Wrong answers — {wrongAnswers.length}
            </h3>
            <table className="report-view-answers-table">
              <thead>
                <tr>
                  <th>Question No.</th>
                  <th>Question</th>
                  <th>Your Answer</th>
                  <th>Correct Answer</th>
                </tr>
              </thead>
              <tbody>
                {wrongAnswers.map((a, idx) => (
                  <tr key={idx} className="incorrect">
                    <td>{getOriginalQuestionNumber(a, allAnswers) ?? '—'}</td>
                    <td>{a.questionText || '—'}</td>
                    <td>{a.selectedAnswerText || '(not answered)'}</td>
                    <td>{a.correctAnswerText || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportView;
